const BASE = "/api/v1";
const RT_KEY = "brvm_admin_rt";

let accessToken: string | null = null;
const tokenListeners = new Set<(t: string | null) => void>();

export function setAccessToken(token: string | null): void {
  accessToken = token;
  tokenListeners.forEach((fn) => fn(token));
}
export const getAccessToken = (): string | null => accessToken;
export function onToken(fn: (t: string | null) => void): () => void {
  tokenListeners.add(fn);
  return () => tokenListeners.delete(fn);
}

export const getStoredRefreshToken = (): string | null => localStorage.getItem(RT_KEY);
export const setStoredRefreshToken = (t: string | null): void => {
  if (t) localStorage.setItem(RT_KEY, t);
  else localStorage.removeItem(RT_KEY);
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

let refreshInFlight: Promise<boolean> | null = null;

export async function tryRefresh(): Promise<boolean> {
  const rt = getStoredRefreshToken();
  if (!rt) return false;
  refreshInFlight ??= fetch(`${BASE}/auth/refresh`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ refreshToken: rt }),
  })
    .then(async (res) => {
      if (!res.ok) return false;
      const data = (await res.json()) as { accessToken: string; refreshToken: string };
      setAccessToken(data.accessToken);
      setStoredRefreshToken(data.refreshToken);
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

interface Options extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** send `body` as-is (string) instead of JSON.stringify */
  rawBody?: boolean;
}

export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const { body, rawBody, headers, ...rest } = opts;
  const build = (): Promise<Response> =>
    fetch(`${BASE}${path}`, {
      ...rest,
      headers: {
        ...(body !== undefined && !rawBody ? { "content-type": "application/json" } : {}),
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
        ...headers,
      },
      body:
        body === undefined
          ? undefined
          : rawBody
            ? (body as BodyInit)
            : JSON.stringify(body),
    });

  let res = await build();
  if (res.status === 401 && !path.startsWith("/auth/")) {
    if (await tryRefresh()) res = await build();
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = (data as { error?: { code?: string; message?: string; details?: unknown } })?.error;
    throw new ApiError(
      res.status,
      err?.code ?? "error",
      err?.message ?? `HTTP ${res.status}`,
      err?.details,
    );
  }
  return data as T;
}
